import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import apiClient from '../apiClient';


export interface BandComponentProps {
  bandName: string[];
  setBandName: React.Dispatch<React.SetStateAction<string[]>>;
  disabled?: boolean;
};

export default function BandComponent(props: BandComponentProps) {
  const {
    bandName,
    setBandName,
    disabled = false,
  } = props;

  const [lteBands, setLteBands] = useState<string[]>([]);
  const [nrBands, setNrBands] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [wifiExpanded, setWifiExpanded] = useState(true);
  const [lteExpanded, setLteExpanded] = useState(false);
  const [nrExpanded, setNrExpanded] = useState(false);

  const wifiBands = ['2.4', '5', '6'];

  useEffect(() => {
    const fetchBands = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/sigcap/api/all-band-filter-list/');
        setLteBands(response.data.lte ?? []);
        setNrBands(response.data.nr ?? []);
      } catch (err) {
        console.error('Failed to fetch band list', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBands();
  }, []);

  const handleWifiChange = (value: string) => {
    setBandName((prev: string[]) =>
      prev.includes(value)
        ? prev.filter((b: string) => b !== value)
        : [...prev, value]
    );
  };

  const handleLteChange = (value: string) => {
    setBandName((prev: string[]) =>
      prev.includes(value)
        ? prev.filter((b: string) => b !== value)
        : [...prev, value]
    );
  };

  const handleNrChange = (value: string) => {
    setBandName((prev: string[]) =>
      prev.includes(value)
        ? prev.filter((b: string) => b !== value)
        : [...prev, value]
    );
  };

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box>
      {/* WiFi collapsible section */}
      <Box sx={{ mb: 1 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => !disabled && setWifiExpanded(prev => !prev)}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>WiFi</Typography>
          <IconButton size="small" disabled={disabled}>
            {wifiExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={wifiExpanded}>
          <Box sx={{ pl: 1 }}>
            {wifiBands.map(item => {
              const value = `wifi:${item}`;
              return (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      size="small"
                      checked={bandName.includes(value)}
                      onChange={() => handleWifiChange(value)}
                      disabled={disabled}
                    />
                  }
                  label={`${item} GHz`}
                  sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'normal', wordBreak: 'break-word' }}
                />
              );
            })}
          </Box>
        </Collapse>
      </Box>

      {/* LTE collapsible section */}
      {lteBands.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => !disabled && setLteExpanded(prev => !prev)}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>LTE 4G</Typography>
            <IconButton size="small" disabled={disabled}>
              {lteExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={lteExpanded}>
            <Box sx={{ pl: 1, height: 360, overflowY: 'auto' }}>
              {lteBands.map((band: string) => {
                  const value = `lte:${band}`;
                  return (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          size="small"
                          checked={bandName.includes(value)}
                          onChange={() => handleLteChange(value)}
                          disabled={disabled}
                        />
                      }
                      label={`Band ${band}`}
                      sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'normal', wordBreak: 'break-word' }}
                    />
                  );
                })}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* NR 5G collapsible section */}
      {nrBands.length > 0 && (
        <Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => !disabled && setNrExpanded(prev => !prev)}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>NR 5G</Typography>
            <IconButton size="small" disabled={disabled}>
              {nrExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={nrExpanded}>
            <Box sx={{ pl: 1, height: 360, overflowY: 'auto' }}>
              {nrBands.map((band: string) => {
                const value = `nr:${band}`;
                return (
                  <FormControlLabel
                    key={value}
                    control={
                      <Checkbox
                        size="small"
                        checked={bandName.includes(value)}
                        onChange={() => handleNrChange(value)}
                        disabled={disabled}
                      />
                    }
                    label={`n${band}`}
                    sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'normal', wordBreak: 'break-word' }}
                  />
                );
              })}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
